import { formatEuro } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner } from '../../../ds/components';
import type { FinancingRecord } from '../types';
import { StagePill } from '../ui/StagePill';
import styles from './record.module.css';

/** The lost state has no frame: reason, who and when, and the team-only note (own text, reported). */
export function LostBanner({ financing }: { financing: FinancingRecord }) {
  const lost = financing.banners.lost;
  if (!lost) return null;
  const by = [lost.byShort, lost.on ? `am ${lost.on}` : null].filter(Boolean).join(' ');
  return (
    <Banner tone="danger" title={`Verloren · ${lost.reason}`}>
      {[by ? `Gesetzt von ${by}.` : null, lost.note].filter(Boolean).join(' ')}
    </Banner>
  );
}

function KeyValues({ rows }: { rows: { label: string; value: string }[] }) {
  return (
    <div className={styles.cardBody}>
      {rows.map((row) => (
        <div key={row.label} className={styles.kv}>
          <span className={styles.kvLabel}>{row.label}</span>
          <span className={styles.kvValue}>{row.value}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * „Übersicht" of the team (Finanzierungen 06/07/08): disbursed banner, „Angebote" (bank · pill · line, rejected
 * and expired rows dimmed), „Konditionen der Zusage" / „Konditionen", „Objekt" for a financing without deal.
 */
export function OverviewMain({ financing }: { financing: FinancingRecord }) {
  const disbursed = financing.banners.disbursed;
  const offers = financing.offers;
  const conditions = financing.conditions;
  const object = financing.object;
  const objectRows = object
    ? [
        { label: 'Art', value: object.typeLabel },
        { label: 'Adresse', value: object.address },
        {
          label: 'Restschuld',
          value: object.remainingDebtCents === null ? null : formatEuro(object.remainingDebtCents),
        },
        { label: 'Aktuelle Bank', value: object.currentBank },
      ].filter((row): row is { label: string; value: string } => row.value !== null && row.value !== '')
    : [];

  return (
    <div className={styles.main}>
      <LostBanner financing={financing} />
      {disbursed ? (
        <div className={styles.successBanner} role="status">
          <span className={styles.successTitle}>{disbursed.title}</span>
          <span className={styles.bannerText}>{disbursed.text}</span>
        </div>
      ) : null}
      {offers && !disbursed ? (
        <section className={styles.card} aria-label="Angebote">
          <h2 className={styles.cardTitle}>Angebote</h2>
          <p className={styles.cardText}>{offers.subtitle}</p>
          <div className={styles.cardBody}>
            {offers.rows.length === 0 ? (
              <div className={clsx(styles.offerRow, styles.muted)}>Noch keine Angebote.</div>
            ) : null}
            {offers.rows.map((row) => (
              <div
                key={row.id}
                className={clsx(
                  styles.offerRow,
                  (row.status === 'rejected' || row.status === 'expired') && styles.offerDim,
                )}
              >
                <span className={styles.offerBank}>{row.bank}</span>
                <span className={styles.offerPill}>
                  <StagePill stage={{ label: row.statusLabel, tone: row.tone }} />
                </span>
                <span className={styles.offerLine}>
                  {[row.line, row.status === 'chosen' ? null : row.dateLabel].filter(Boolean).join(' · ')}
                </span>
              </div>
            ))}
          </div>
        </section>
      ) : null}
      {conditions ? (
        <section className={styles.card} aria-label={conditions.title}>
          <h2 className={styles.cardTitle}>{conditions.title}</h2>
          <KeyValues
            rows={[
              ...conditions.rows,
              ...(conditions.notForCustomers
                ? [{ label: conditions.notForCustomers.label, value: conditions.notForCustomers.text }]
                : []),
            ]}
          />
        </section>
      ) : null}
      {object ? (
        <section className={styles.card} aria-label="Objekt">
          <h2 className={styles.cardTitle}>Objekt</h2>
          <p className={styles.cardText}>{object.subtitle}</p>
          <KeyValues rows={objectRows} />
        </section>
      ) : null}
    </div>
  );
}
